import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  MessageSquare,
  Bot,
  FolderOpen,
  Settings,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Circle,
  Calendar,
  Rocket,
  MessageCircle,
  Users,
  Shield,
  CheckSquare,
  ClipboardList,
  Cpu,
  Sparkles,
  LayoutDashboard,
  FileText,
  Terminal,
  TrendingUp,
  Scale,
  DollarSign,
  FileSignature,
  Mail,
  Brain,
  Video,
  Puzzle,
} from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';

interface Agent {
  id: string;
  name: string;
  status: 'running' | 'idle' | 'error';
  sessions: { id: string; title: string }[];
}

// Placeholder demo data
const demoAgents: Agent[] = [
  {
    id: '1',
    name: 'סוכן תמיכה',
    status: 'running',
    sessions: [
      { id: 's1', title: 'שיחה #1024' },
      { id: 's2', title: 'שיחה #1025' },
    ],
  },
  {
    id: '2',
    name: 'סוכן מכירות',
    status: 'idle',
    sessions: [{ id: 's3', title: 'שיחה #512' }],
  },
  {
    id: '3',
    name: 'סוכן ניתוח',
    status: 'error',
    sessions: [],
  },
];

const navItems = [
  { to: '/', icon: Home, label: 'לוח בקרה' },
  { to: '/chat', icon: MessageSquare, label: 'צ\'אט' },
  { to: '/teamchats', icon: Users, label: 'צ\'אט קבוצתי' },
  { to: '/agents', icon: Bot, label: 'סוכנים' },
  { to: '/projects', icon: FolderOpen, label: 'פרויקטים' },
  { to: '/autonomous', icon: Sparkles, label: 'פרויקט אוטונומי' },
  { to: '/progress', icon: LayoutDashboard, label: 'לוח התקדמות' },
  { to: '/spawns', icon: Cpu, label: 'סוכנים פעילים' },
  { to: '/automation', icon: Calendar, label: 'אוטומציה' },
  { to: '/channels', icon: MessageCircle, label: 'ערוצים' },
  { to: '/approvals', icon: CheckSquare, label: 'אישורים' },
  { to: '/deploy', icon: Rocket, label: 'פריסה' },
  { to: '/audit', icon: ClipboardList, label: 'יומן ביקורת' },
  { to: '/transcripts', icon: FileText, label: 'Transcripts' },
  { to: '/hooks', icon: Terminal, label: 'Hooks' },
  { to: '/settings', icon: Settings, label: 'הגדרות' },
];

const advancedNavItems = [
  { to: '/trends', icon: TrendingUp, label: 'ארביטראז\' טרנדים' },
  { to: '/legal', icon: Scale, label: 'סוכן משפטי' },
  { to: '/monetization', icon: DollarSign, label: 'מונטיזציה' },
  { to: '/documents', icon: FileSignature, label: 'מחולל מסמכים' },
  { to: '/email-agent', icon: Mail, label: 'סוכן אימייל' },
  { to: '/cross-project', icon: Brain, label: 'מוח חוצה-פרויקטים' },
  { to: '/demo-recorder', icon: Video, label: 'מקליט הדגמות' },
  { to: '/plugins', icon: Puzzle, label: 'תוספים' },
];

export const Sidebar: React.FC = () => {
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const currentUser = useAuthStore((s) => s.user);
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set(['1']));

  const toggleAgent = (id: string) => {
    setExpandedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-logo">
        <Bot size={24} color="var(--accent)" />
        <h1>BotBoss</h1>
      </div>

      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <item.icon size={18} />
            <span className="nav-label">{item.label}</span>
          </NavLink>
        ))}
        {currentUser?.role === 'admin' && (
          <NavLink
            to="/admin"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <Shield size={18} />
            <span className="nav-label">{'ניהול'}</span>
          </NavLink>
        )}
      </nav>

      <div className="sidebar-section">
        <div className="sidebar-section-title">כלים מתקדמים</div>
      </div>

      <nav className="sidebar-nav">
        {advancedNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <item.icon size={18} />
            <span className="nav-label">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-section">
        <div className="sidebar-section-title">סוכנים פעילים</div>
      </div>

      <div className="agent-tree">
        {demoAgents.map((agent) => (
          <div key={agent.id} className="agent-tree-item">
            <button
              className="agent-tree-header"
              onClick={() => toggleAgent(agent.id)}
            >
              {expandedAgents.has(agent.id) ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
              <span className={`agent-status-dot ${agent.status}`} />
              <span className="agent-tree-name">{agent.name}</span>
            </button>
            {expandedAgents.has(agent.id) && agent.sessions.length > 0 && (
              <div className="agent-sessions">
                {agent.sessions.map((session) => (
                  <NavLink
                    key={session.id}
                    to={`/chat/${session.id}`}
                    className="session-item"
                  >
                    <Circle size={6} />
                    <span>{session.title}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <button className="sidebar-collapse-btn" onClick={toggleSidebar}>
        {sidebarCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </button>
    </aside>
  );
};
